import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const TECH = [
  { n: "01", title: "Carbonfly Wrap", desc: "An ultra-light carbon crown that wraps into the skirt, freeing up weight to push MOI and forgiveness to new limits." },
  { n: "02", title: "Facewrap Technology", desc: "A high-strength maraging steel face insert that wraps into the crown and sole to flex more, generating faster ball speeds." },
  { n: "03", title: "Precision Milled Faces", desc: "Every PLD putter face is CNC-milled from solid billet for a soft, consistent feel and pure, repeatable roll." },
  { n: "04", title: "Custom Fitting", desc: "Colour-coded lie angle system and shaft matrix ensure every club is built precisely to your swing dynamics." },
];

// Fallback used when the admin hasn't filled in `technology_banner` yet.
const DEFAULTS = {
  image: "https://images.pexels.com/photos/33855229/pexels-photo-33855229.jpeg",
  kicker: "Innovation",
  heading: "The Science\nOf Better Golf",
};

const renderHeading = (text) =>
  (text || "").split("\n").map((line, i, arr) => (
    <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
  ));

export default function Technology() {
  const settings = useSiteSettings();
  const banner = settings?.technology_banner || {};
  const image = banner.image || DEFAULTS.image;
  const kicker = banner.kicker || DEFAULTS.kicker;
  const heading = banner.heading || DEFAULTS.heading;
  return (
    <div data-testid="technology-page">
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={image} alt="Technology" className="absolute inset-0 w-full h-full" style={{ objectFit: banner.image_fit || "cover", objectPosition: banner.image_position || "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/30" />
        <div className="container-ping relative h-full flex flex-col justify-end pb-16">
          {kicker && <span className="label-caps text-[#E31837] bg-white px-3 py-1 inline-block w-fit mb-4" data-testid="tech-hero-kicker">{kicker}</span>}
          <h1 className="heading-1 text-white max-w-3xl" data-testid="tech-hero-heading">{renderHeading(heading)}</h1>
          {banner.text && <p className="mt-4 text-white/75 max-w-xl text-base leading-relaxed">{banner.text}</p>}
        </div>
      </section>

      <section className="container-ping py-20">
        <p className="max-w-2xl text-lg text-muted-foreground leading-relaxed">
          Since 1959, PING has been driven by a relentless engineering culture — building clubs around the golfer, not the other way around. Here is a look at the technology that powers our equipment.
        </p>
        <div className="mt-14 grid md:grid-cols-2 gap-px bg-border border border-border">
          {TECH.map((t) => (
            <div key={t.n} className="bg-white p-10 group hover:bg-[#0A0A0A] btn-transition" data-testid={`tech-${t.n}`}>
              <span className="font-head text-5xl font-black text-[#E31837]">{t.n}</span>
              <h3 className="heading-3 mt-4 group-hover:text-white btn-transition">{t.title}</h3>
              <p className="mt-3 text-muted-foreground group-hover:text-white/70 btn-transition leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#111111] text-white py-16">
        <div className="container-ping flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="heading-2 text-white">Ready To Get Fit?</h2>
            <p className="text-white/70 mt-2">Experience the technology first-hand at a PING India fitting studio.</p>
          </div>
          <Link to="/dealers" className="bg-[#E31837] text-white px-10 py-4 label-caps btn-transition hover:bg-[#b3122c] active:scale-95 flex items-center gap-2 shrink-0" data-testid="tech-fitting-cta">
            Find A Studio <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
